import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import { Table, TableRow, TableHeader, TableCell } from "@tiptap/extension-table";
import {
  Bold, Italic, Underline as UnderlineIcon,
  Heading1, Heading2, Heading3,
  List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Link2, Table as TableIcon, Undo2, Redo2, Eraser,
} from "lucide-react";
import { useEffect } from "react";

/**
 * TipTap-based rich-text editor for the ONE module.
 *
 * Feature set (deliberately curated):
 *   Bold, Italic, Underline
 *   Headings (H1/H2/H3)
 *   Bullet & Numbered lists
 *   Text alignment (left / center / right / justify)
 *   Tables (insert / add row / add column / delete)
 *   Hyperlinks
 *   Undo / Redo
 *   Word-paste is handled by TipTap by default (HTML paste)
 *   Clear formatting
 *
 * Deliberately excluded: image embedding, code blocks, emojis,
 * mentions, collaboration, video.
 */
function Btn({ onClick, active, disabled, title, testid: btnId, children }) {
  return (
    <button
      type="button"
      data-testid={btnId}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`h-8 min-w-[32px] px-2 flex items-center justify-center border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed ${
        active ? "bg-[#F97316] text-white border-[#F97316] hover:bg-[#EA580C]" : "bg-white"
      }`}
    >
      {children}
    </button>
  );
}

export default function RichTextEditor({
  value = "",
  onChange = () => {},
  readOnly = false,
  minHeight = 260,
  testid = "one-body-editor",
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // StarterKit v3 ships Underline + Link built-in. Disable them here
        // so we can re-add our own configured versions (autolink links +
        // no code-block feature exposed via toolbar) without triggering
        // the "Duplicate extension names" warning.
        heading: { levels: [1, 2, 3] },
        underline: false,
        link: false,
      }),
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value || "",
    editable: !readOnly,
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
  });

  // Keep external `value` in sync when the parent replaces it (e.g. after fetch).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || "") !== current) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  // Sync editable state changes.
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!readOnly);
  }, [readOnly, editor]);

  if (!editor) {
    return (
      <div
        data-testid={`${testid}-loading`}
        className="border border-slate-300 bg-slate-50 text-slate-400 text-xs px-3 py-4"
      >
        Loading editor…
      </div>
    );
  }

  function promptLink() {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Enter URL (leave blank to remove):", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }

  function insertTable() {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }

  return (
    <div
      data-testid={testid}
      className="border border-slate-300 bg-white flex flex-col"
    >
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 bg-slate-50 p-2">
          <Btn
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive("bold")}
            title="Bold (Ctrl+B)"
            testid="rte-bold"
          ><Bold size={13} /></Btn>
          <Btn
            onClick={() => editor.chain().focus().toggleItalic().run()}
            active={editor.isActive("italic")}
            title="Italic (Ctrl+I)"
            testid="rte-italic"
          ><Italic size={13} /></Btn>
          <Btn
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            active={editor.isActive("underline")}
            title="Underline (Ctrl+U)"
            testid="rte-underline"
          ><UnderlineIcon size={13} /></Btn>

          <div className="mx-1 w-px h-6 bg-slate-300" />

          <Btn
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            active={editor.isActive("heading", { level: 1 })}
            title="Heading 1"
            testid="rte-h1"
          ><Heading1 size={13} /></Btn>
          <Btn
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            active={editor.isActive("heading", { level: 2 })}
            title="Heading 2"
            testid="rte-h2"
          ><Heading2 size={13} /></Btn>
          <Btn
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            active={editor.isActive("heading", { level: 3 })}
            title="Heading 3"
            testid="rte-h3"
          ><Heading3 size={13} /></Btn>

          <div className="mx-1 w-px h-6 bg-slate-300" />

          <Btn
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive("bulletList")}
            title="Bullet list"
            testid="rte-ul"
          ><List size={13} /></Btn>
          <Btn
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            active={editor.isActive("orderedList")}
            title="Numbered list"
            testid="rte-ol"
          ><ListOrdered size={13} /></Btn>

          <div className="mx-1 w-px h-6 bg-slate-300" />

          <Btn
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
            active={editor.isActive({ textAlign: "left" })}
            title="Align left"
            testid="rte-align-left"
          ><AlignLeft size={13} /></Btn>
          <Btn
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
            active={editor.isActive({ textAlign: "center" })}
            title="Align center"
            testid="rte-align-center"
          ><AlignCenter size={13} /></Btn>
          <Btn
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
            active={editor.isActive({ textAlign: "right" })}
            title="Align right"
            testid="rte-align-right"
          ><AlignRight size={13} /></Btn>
          <Btn
            onClick={() => editor.chain().focus().setTextAlign("justify").run()}
            active={editor.isActive({ textAlign: "justify" })}
            title="Justify"
            testid="rte-align-justify"
          ><AlignJustify size={13} /></Btn>

          <div className="mx-1 w-px h-6 bg-slate-300" />

          <Btn
            onClick={promptLink}
            active={editor.isActive("link")}
            title="Insert / edit link"
            testid="rte-link"
          ><Link2 size={13} /></Btn>
          <Btn
            onClick={insertTable}
            title="Insert 3×3 table"
            testid="rte-table"
          ><TableIcon size={13} /></Btn>

          {editor.can().addRowAfter() && (
            <>
              <Btn
                onClick={() => editor.chain().focus().addRowAfter().run()}
                title="Add row after"
                testid="rte-row-add"
              >+R</Btn>
              <Btn
                onClick={() => editor.chain().focus().addColumnAfter().run()}
                title="Add column after"
                testid="rte-col-add"
              >+C</Btn>
              <Btn
                onClick={() => editor.chain().focus().deleteTable().run()}
                title="Delete table"
                testid="rte-table-del"
              >×T</Btn>
            </>
          )}

          <div className="mx-1 w-px h-6 bg-slate-300" />

          <Btn
            onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
            title="Clear formatting"
            testid="rte-clear"
          ><Eraser size={13} /></Btn>

          <div className="mx-1 w-px h-6 bg-slate-300" />

          <Btn
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            title="Undo"
            testid="rte-undo"
          ><Undo2 size={13} /></Btn>
          <Btn
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            title="Redo"
            testid="rte-redo"
          ><Redo2 size={13} /></Btn>
        </div>
      )}

      <EditorContent
        editor={editor}
        className="rte-content px-4 py-3 text-sm text-slate-900 outline-none"
        style={{ minHeight }}
      />

      <style>{`
        .rte-content .ProseMirror { outline: none; min-height: ${minHeight}px; }
        .rte-content .ProseMirror p { margin: 0 0 0.6em; line-height: 1.55; }
        .rte-content .ProseMirror h1 { font-size: 1.5rem; font-weight: 700; margin: 0.6em 0 0.4em; }
        .rte-content .ProseMirror h2 { font-size: 1.25rem; font-weight: 700; margin: 0.5em 0 0.3em; }
        .rte-content .ProseMirror h3 { font-size: 1.05rem; font-weight: 700; margin: 0.4em 0 0.25em; }
        .rte-content .ProseMirror ul { list-style: disc; padding-left: 1.5em; margin-bottom: 0.6em; }
        .rte-content .ProseMirror ol { list-style: decimal; padding-left: 1.5em; margin-bottom: 0.6em; }
        .rte-content .ProseMirror a { color: #F97316; text-decoration: underline; }
        .rte-content .ProseMirror table {
          border-collapse: collapse; margin: 0.75em 0; width: 100%;
          table-layout: fixed;
        }
        .rte-content .ProseMirror th, .rte-content .ProseMirror td {
          border: 1px solid #cbd5e1; padding: 6px 10px; vertical-align: top;
          font-size: 0.85rem; position: relative;
        }
        .rte-content .ProseMirror th { background: #f8fafc; font-weight: 700; }
        .rte-content .ProseMirror .selectedCell:after {
          content: ''; position: absolute; inset: 0;
          background: rgba(249, 115, 22, 0.12); pointer-events: none;
        }
      `}</style>
    </div>
  );
}
